import { XMarkIcon } from "@heroicons/react/24/outline";
import { Check } from "lucide-react";
import { useState } from "react";
import { Badge } from "@/core/components/ui/badge";
import { Button } from "@/core/components/ui/button";
import { ScrollArea } from "@/core/components/ui/scroll-area";
import { Stack } from "@/core/components/ui/stack";
import {
  Tabs,
  TabsContent,
  TabsListWithIndicator,
  TabsTrigger,
} from "@/core/components/ui/tabs";
import { Typography } from "@/core/components/ui/typography";
import { formatRelativeTime } from "@/core/utils/date";
import {
  EditCard,
  LoadError,
} from "@/payroll/ai-script/ai-script-persistent-edits/shared";
import { formatCoordinate } from "@/payroll/ai-script/ai-script-spreadsheet-viewer/utils";
import { CellCommentThreadView } from "./cell-comment-thread";
import { useCellCommentThreads } from "./query";
import { CELL_COMMENT_PLACEMENT, type ICellCommentThread } from "./types";
import { useCellCommentActions } from "./use-cell-comment-actions";

const TAB = { open: "open", resolved: "resolved" } as const;
type CommentsTab = (typeof TAB)[keyof typeof TAB];

const isPlaced = (thread: ICellCommentThread) =>
  thread.placement.status === CELL_COMMENT_PLACEMENT.anchored ||
  thread.placement.status === CELL_COMMENT_PLACEMENT.positional;

const placementLabel = (thread: ICellCommentThread) => {
  const { row, col } = thread.placement;
  return isPlaced(thread) && row != null && col != null
    ? formatCoordinate([col - 1, row - 1])
    : null;
};

const lastActivity = (thread: ICellCommentThread) =>
  thread.comments.at(-1)?.created_at ?? thread.created_at;

const ThreadRow = ({
  thread,
  businessId,
  runId,
  onGoToThread,
}: {
  thread: ICellCommentThread;
  businessId: string | null;
  runId: string;
  onGoToThread: (thread: ICellCommentThread) => void;
}) => {
  const [expanded, setExpanded] = useState(false);
  const actions = useCellCommentActions(businessId, runId);
  const first = thread.comments[0];
  const replies = thread.comments.length - 1;
  const address = placementLabel(thread);
  const activate = () =>
    address ? onGoToThread(thread) : setExpanded((value) => !value);

  return (
    <EditCard>
      {/* A thread on a cell opens on the grid, where its card holds the whole
          conversation. One whose cell is gone this run has nowhere to go, so it
          expands here instead. A real button, so the controls an expanded
          thread shows are not buried inside one. */}
      <button
        type="button"
        onClick={activate}
        aria-expanded={address ? undefined : expanded}
        aria-label={
          address
            ? `Go to comment on ${thread.sheet_name} ${address}`
            : `${expanded ? "Hide" : "Show"} comment on ${thread.sheet_name}`
        }
        className="-m-1 flex min-w-0 flex-col gap-2 rounded-lg p-1 text-left focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
      >
        <Stack direction="row" align="center" gap="sm" fullWidth>
          <Typography variant="subtitleW3" className="min-w-0 truncate">
            {thread.sheet_name}
          </Typography>
          {address ? (
            <Typography
              variant="bodyW3"
              textColor="tertiary"
              className="shrink-0 font-mono text-[12px]"
            >
              {address}
            </Typography>
          ) : (
            <Badge variant="gray" size="sm" className="shrink-0">
              Cell not found this run
            </Badge>
          )}
        </Stack>
        {!expanded && (
          <Typography
            variant="bodyW2"
            className="line-clamp-2 whitespace-pre-wrap break-words"
            textColor={first?.deleted ? "tertiary" : "primary"}
          >
            {first?.deleted ? "Comment deleted" : first?.body}
          </Typography>
        )}
        <Typography
          variant="bodyW3"
          textColor="tertiary"
          className="text-[12px]"
        >
          {[
            first?.author.full_name,
            replies > 0
              ? `${replies} ${replies === 1 ? "reply" : "replies"}`
              : null,
            formatRelativeTime(lastActivity(thread)),
          ]
            .filter(Boolean)
            .join(" · ")}
        </Typography>
      </button>
      {expanded && (
        <Stack gap="sm" align="stretch" fullWidth>
          <CellCommentThreadView
            thread={thread}
            isUpdating={actions.isUpdating}
            onDeleteComment={(commentId) =>
              void actions.deleteComment(thread.id, commentId)
            }
            onReopen={() => void actions.reopen(thread.id)}
          />
          {!thread.resolved_at && (
            <Button
              variant="text"
              size="xs"
              className="gap-1 self-start px-1 [&_svg]:size-3.5"
              disabled={actions.isUpdating}
              onClick={() => void actions.resolve(thread.id)}
            >
              <Check aria-hidden />
              Resolve
            </Button>
          )}
        </Stack>
      )}
    </EditCard>
  );
};

/**
 * Every comment thread on this pay period's workbook, open first. Picking one
 * takes the grid to its cell and opens its card there.
 */
export const CellCommentsSidebar = ({
  businessId,
  runId,
  onGoToThread,
  onClose,
}: {
  businessId: string | null;
  runId: string;
  onGoToThread: (thread: ICellCommentThread) => void;
  onClose: () => void;
}) => {
  const {
    data: threads,
    isLoading,
    isError,
    refetch,
  } = useCellCommentThreads(businessId, runId);
  const [tab, setTab] = useState<CommentsTab>(TAB.open);
  const sorted = [...(threads ?? [])].sort((left, right) =>
    lastActivity(right).localeCompare(lastActivity(left)),
  );
  const open = sorted.filter((thread) => !thread.resolved_at);
  const resolved = sorted.filter((thread) => !!thread.resolved_at);

  return (
    <Stack fullWidth align="stretch" className="h-full min-h-0" gap="none">
      <Stack gap="sm" fullWidth className="shrink-0 bg-bg-secondary/50 p-6">
        <Stack
          direction="row"
          justify="between"
          align="center"
          fullWidth
          gap="sm"
        >
          <Typography className="min-w-0 truncate text-2xl font-bold">
            Comments
          </Typography>
          <Button variant="unstyled" aria-label="Close" onClick={onClose}>
            <XMarkIcon className="size-6 text-secondary-text" />
          </Button>
        </Stack>
        <Typography variant="bodyW1" textColor="secondary">
          {open.length === 0
            ? "No open comments on this pay period"
            : `${open.length} open ${open.length === 1 ? "thread" : "threads"} on this pay period`}
        </Typography>
      </Stack>

      <Tabs
        value={tab}
        onValueChange={(value) => setTab(value as CommentsTab)}
        className="flex min-h-0 flex-1 flex-col"
      >
        <Stack className="shrink-0 border-b border-stroke-tertiary">
          <TabsListWithIndicator size="small" hideBottomBorder>
            <TabsTrigger value={TAB.open} count={open.length} className="px-3">
              Open
            </TabsTrigger>
            <TabsTrigger
              value={TAB.resolved}
              count={resolved.length}
              className="px-3"
            >
              Resolved
            </TabsTrigger>
          </TabsListWithIndicator>
        </Stack>
        {[TAB.open, TAB.resolved].map((panel) => (
          <TabsContent
            key={panel}
            value={panel}
            className="mt-0 flex min-h-0 flex-1 flex-col data-[state=inactive]:hidden"
          >
            <ScrollArea className="min-h-0 flex-1">
              <Stack
                gap="sm"
                align="stretch"
                className="max-w-[var(--simple-sidebar-width,436px)] px-6 py-5"
              >
                {isLoading ? (
                  <Typography variant="bodyW2" textColor="tertiary">
                    Loading comments…
                  </Typography>
                ) : isError ? (
                  <LoadError
                    message="Couldn't load comments."
                    onRetry={() => void refetch()}
                  />
                ) : (panel === TAB.open ? open : resolved).length === 0 ? (
                  <Typography variant="bodyW2" textColor="tertiary">
                    {panel === TAB.open
                      ? "Right-click a cell and choose Comment to start a thread."
                      : "Resolved threads show up here."}
                  </Typography>
                ) : (
                  (panel === TAB.open ? open : resolved).map((thread) => (
                    <ThreadRow
                      key={thread.id}
                      thread={thread}
                      businessId={businessId}
                      runId={runId}
                      onGoToThread={onGoToThread}
                    />
                  ))
                )}
              </Stack>
            </ScrollArea>
          </TabsContent>
        ))}
      </Tabs>
    </Stack>
  );
};
